import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getUser,
  updateDipUser,
  getAllRoles,
} from "../../api/Users";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import LegacyUserForm from "../../components/user/LegacyUserForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByUser } from "../../api/BillingGroups"; // Import the API function

/**
 * The production edit page (commit 370850a): role and billing group only. Shown for an
 * agreement without SCO forms (PBI #385); the SCO EditUserPage serves the rest.
 */
const LegacyEditUserPage = () => {
  const { getAccessToken } = useAuth();
  const { userPublicId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    handleGetUser();
    handleGetBillingGroups(); // Fetch billing groups
    handleGetRoles(); // Fetch roles
  }, [userPublicId]);

  const handleGetUser = async () => {
    try {
      const accessToken = await getAccessToken();
      const newUser = await getUser(userPublicId, accessToken);

      if (Array.isArray(newUser.roles)) {
        newUser.roles = newUser.roles.map((r) =>
        typeof r === "string" ? r : r.name
        );        
      } else {
        newUser.roles = [];
      }
      
      setUser(newUser);
      setLoadingUser(false);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setLoadingUser(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByUser(userPublicId, accessToken);
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleGetRoles = async () => {
    try {
      const accessToken = await getAccessToken();
      const roles = await getAllRoles(accessToken);
      setRoles(roles);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const handleBillingGroupChange = (option) => {
    const selectedGroup = billingGroups.find((group) => group.name === option);
    if (!selectedGroup) return;
    setUser((prevUser) => ({
      ...prevUser,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  };

  const handleRoleChange = (option) => {
    const selectedRole = roles.find((group) => group.name === option);
    if (!selectedRole) return;
    setUser((prevUser) => ({
      ...prevUser,
      roleName: selectedRole.roleName,
    }));
    setHasChanges(true);
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!user.billingGroupPublicId) {
        console.error("Missing billingGroupPublicId! API call might fail.");
        return;
      }

      if (user.billingGroupPublicId) {
        const updatedUser = await updateDipUser(
          user.billingGroupPublicId,
          user.roles,   
          user.userPublicId,       
          accessToken
        );

        setUser(updatedUser); 
      }

      setHasChanges(false); 

      handleGetUser();

      toast.success("User updated successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error("Failed to update user");
    }
    navigate(-1);
  };

  const saveAndExit = () => {
    handleUpdateUser();
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Users"} loading={loadingUser} />
          </div>
          <div className={`${loadingUser ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit user: {user?.displayName}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={saveAndExit}
                >
                  Update
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <LegacyUserForm
                user={user || {}}
                setUser={setUser}
                setHasChanges={setHasChanges}
                disabled={true}
                billingGroups={billingGroups}
                roles={roles}
                onBillingGroupChange={handleBillingGroupChange}
                onRoleChange={handleRoleChange}
              /> 

            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default LegacyEditUserPage;
